import { GridSelectionModel } from "@mui/x-data-grid-pro";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, collectionFetcher } from "services/api.service";
import { Inventory } from "types/inventory";
import { useAlert } from "./useAlert";
import { useConfirmation } from "./useConfirmation";
import { warehouseLocationValidation } from "validations/inventory";
import useSWR from "swr";
import { LocationManagementLocationType } from "types/locationManagement/locationType";

type SkipActions = Record<string, string[]>;

export default function useScan() {
  const { search } = useLocation();
  const { alertSuccess, alertError, alertInfo } = useAlert();
  const { confirm } = useConfirmation();
  const addedFromQuery = useRef(false);

  const query = useMemo(() => new URLSearchParams(search), [search]);
  const [inventories, setInventories] = useState<Inventory[]>([]);
  const [selectedInventories, setSelectedInventories] = useState<GridSelectionModel>([]);
  const { data: locationTypes } = useSWR<LocationManagementLocationType[]>(
    "/location_management/location_types?page[size]=100",
    collectionFetcher,
  );

  const moveInventories = useCallback(
    (location: string, skipActions?: SkipActions) => {
      const payload = selectedInventories.map((inventoryId) => ({
        id: inventoryId,
        warehouse_location: location,
        ...(skipActions?.[String(inventoryId)] ?? []).reduce<Record<string, boolean>>(
          (acc, skipParam) => ({ ...acc, [skipParam]: true }),
          {},
        ),
      }));
      api
        .put("inventories/bulk/edit", { inventories: payload })
        .then(() => {
          setInventories((current) => current.filter(({ id }) => !selectedInventories.includes(id)));
          alertSuccess("Items have been moved successfully");
        })
        .catch((error) => {
          if (error.response.status === 422) {
            if (error.response.data.errors.skip_param) {
              confirm({
                handler: () => {
                  const id = error.response.data.errors.id;
                  const newParam = error.response.data.errors.skip_param;
                  const inventoryParamsArray = [...(skipActions?.[String(id)] ?? []), newParam];
                  moveInventories(location, { ...skipActions, [id]: inventoryParamsArray });
                },
              });
            } else {
              alertError(error.response.data.errors.message);
            }
          }
        });
    },
    [alertError, alertSuccess, confirm, selectedInventories],
  );

  const { handleSubmit, getFieldProps, setFieldValue, errors, resetForm } = useFormik({
    initialValues: { location: query.get("warehouse_location") ?? "" },
    validationSchema: warehouseLocationValidation(locationTypes),
    onSubmit: (values) => {
      confirm({
        message: `Are you sure you want to move item to "${values.location}"`,
        handler: () => {
          moveInventories(values.location);
          resetForm();
        },
      });
    },
  });

  const getInventoryByWarehouseLocation = (warehouseLocation: string) => {
    return api
      .get(`/inventories_by_warehouse_location?warehouse_location=${warehouseLocation}`)
      .then(({ data }) => {
        setInventories((current) => [...current, ...data.inventories]);
        setSelectedInventories((current) => [
          ...current,
          ...data.inventories.map((inventory: Inventory) => inventory.id),
        ]);
      })
      .catch((error) => {
        if (error.response?.status === 404) {
          alertError(`Can't find inventory with location ${warehouseLocation}`);
        }
      });
  };

  const addInventory = useCallback(
    (inventoryId: string | number) => {
      if (inventories.find(({ id }) => id.toString() === inventoryId.toString())) {
        alertInfo(`Inventory ${inventoryId} is already added.`);
        return Promise.resolve();
      }
      return api
        .get(`/inventories/${inventoryId}`)
        .then(({ data }) => {
          setInventories((current) => [...current, data]);
          setSelectedInventories((current) => [...current, data.id]);
        })
        .catch((error) => {
          if (error.response?.status === 404) {
            alertError(`Can't find inventory with id ${inventoryId}`);
          }
        });
    },
    [alertError, alertInfo, inventories],
  );

  const removeInventory = useCallback((inventoryId: string | number) => {
    setInventories((current) => current.filter(({ id }) => id != inventoryId));
  }, []);

  const invoiceInventory = () => {
    api
      .get("/location_management/locations/lookup", { params: { code: "invoiced" } })
      .then(({ data }) => {
        const payload = selectedInventories.map((id) => ({ id, state: "invoiced", warehouseLocation: data.code }));
        return api.put("inventories/bulk/edit", { inventories: payload });
      })
      .then(() => {
        setInventories((current) => current.filter(({ id }) => !selectedInventories.includes(id)));
        alertSuccess("Inventory has been successfully invoiced.");
      })
      .catch(() => {
        alertError("There was an error, please try again");
      });
  };

  const handleExScanInput = useCallback((event: any, key: string, formikHandler: (event: any) => void) => {
    event.persist();
    const value = event.target.value;
    try {
      const url = new URL(value);
      event.target.value = url.searchParams.get(key);
      formikHandler(event);
    } catch (_) {
      formikHandler(event);
    }
  }, []);

  useEffect(() => {
    if (addedFromQuery.current) return;

    const inventoryId = query.get("inventory_id");
    if (!inventoryId) return;

    addedFromQuery.current = true;
    addInventory(inventoryId);
  }, [addInventory, query]);

  return {
    inventories,
    selectedInventories,
    setSelectedInventories,
    addInventory,
    getInventoryByWarehouseLocation,
    removeInventory,
    handleExScanInput,
    submitForm: handleSubmit,
    errors,
    invoiceInventory,
    locationInputProps: getFieldProps("location"),
    setLocation: (value: string) => setFieldValue("location", value),
  };
}
